import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight, Loader2, Box, User, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SharedListing — public property page at /share/[id] or /p/[id]
 * SEO-optimized with dynamic meta tags, agent attribution, and lead capture.
 */
export default function SharedListing() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("mls");
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/share/${id}`)
      .then((r) => {
        if (!mounted) return;
        setListing(r.data);
        setLoading(false);
        // SEO: set document title dynamically
        const agent = r.data?.agent?.name || "";
        const tone = r.data?.tone || "Property";
        const addr = r.data?.mls?.slice(0, 60) || `${tone} Listing`;
        document.title = `${addr} — ListWorks PRO${agent ? ` | ${agent}` : ""}`;
        // OG meta tags
        let desc = document.querySelector('meta[name="description"]');
        if (!desc) { desc = document.createElement("meta"); desc.name = "description"; document.head.appendChild(desc); }
        desc.content = `AI-rewritten listing: ${r.data?.mls?.slice(0, 160) || "Professional real estate listing copy generated by ListWorks PRO."}`;
      })
      .catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  const TABS = [
    { key: "mls", label: "MLS", emoji: "🏡" },
    { key: "instagram", label: "Instagram", emoji: "📸" },
    { key: "facebook", label: "Facebook", emoji: "📘" },
    { key: "headlines", label: "Headlines", emoji: "✏️" },
    { key: "email", label: "Email", emoji: "✉️" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-oat flex items-center justify-center" data-testid="share-loading">
        <Loader2 className="w-6 h-6 animate-spin text-vermillion" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-oat flex flex-col items-center justify-center px-6 text-center" data-testid="share-not-found">
        <h1 className="font-display text-4xl mb-3">This listing flew the coop.</h1>
        <p className="text-ink/65 mb-8">The shared link is invalid or expired.</p>
        <Link to="/" className="btn-vermillion px-7 py-4 font-heading text-sm uppercase tracking-[0.15em]">
          Make Your Own Free →
        </Link>
      </div>
    );
  }

  const headlines = Array.isArray(listing.headlines) ? listing.headlines : [];
  const tabContent = {
    mls: listing.mls,
    instagram: listing.instagram,
    facebook: listing.facebook,
    headlines: headlines.map((h, i) => `${i + 1}. ${h}`).join("\n\n"),
    email: listing.email,
  };

  return (
    <div className="min-h-screen bg-oat" data-testid="shared-listing-page">
      {/* Top bar with branded CTA — most important pixel on the page */}
      <header className="sticky top-0 z-30 bg-oat/95 backdrop-blur-md border-b border-ink/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-lg tracking-tight" data-testid="share-logo">
            ListWorks <span className="text-vermillion italic">/PRO</span>
          </Link>
          <Link
            to="/#playground"
            data-testid="share-cta-top"
            className="btn-vermillion px-5 py-2.5 font-heading text-xs uppercase tracking-[0.18em] hover:-translate-y-0.5 transition-transform"
          >
            Try Free → 8 Sec
          </Link>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
        {/* Eyebrow + headline */}
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-vermillion" strokeWidth={2} />
          <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-vermillion">
            AI-REWRITTEN IN 8 SECONDS · {String(listing.tone || "Modern").toUpperCase()} TONE
          </span>
        </div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] mb-12">
          <span className="font-light">Same listing.</span>{" "}
          <span className="italic">Wildly different result.</span>
        </h1>

        {/* Before / After grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-ink/15 border border-ink/15 mb-12">
          {/* BEFORE */}
          <div className="bg-stone-100 p-7 md:p-9">
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-ink/45 mb-4">
              ❌ Before — boring MLS draft
            </div>
            <p className="font-body text-base leading-relaxed text-ink/70 whitespace-pre-wrap">
              {listing.raw_listing}
            </p>
          </div>

          {/* AFTER */}
          <div className="bg-coal text-oat p-7 md:p-9">
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-vermillion">
                ✅ After — listworks.pro
              </span>
            </div>

            {/* Tab selector */}
            <div className="flex flex-wrap gap-1 mb-5">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  data-testid={`share-tab-${t.key}`}
                  className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-all ${
                    tab === t.key
                      ? "bg-vermillion text-oat"
                      : "bg-oat/10 text-oat/70 hover:bg-oat/20"
                  }`}
                >
                  <span className="mr-1.5">{t.emoji}</span>
                  {t.label}
                </button>
              ))}
            </div>

            <p className="font-body text-base leading-relaxed text-oat/95 whitespace-pre-wrap min-h-[200px]">
              {tabContent[tab]}
            </p>
          </div>
        </div>

        {/* 360° Virtual Tour embed */}
        {listing?.virtual_tour_url && (
          <div className="mb-12 bg-white border-2 border-vermillion/40 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-vermillion text-white w-10 h-10 rounded-full flex items-center justify-center">
                <Box className="w-5 h-5" />
              </div>
              <div>
                <span className="font-heading text-lg uppercase tracking-[0.15em] text-ink">360° Virtual Tour</span>
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-ink/50">Explore the property in stunning 3D</p>
              </div>
            </div>
            <div className="relative w-full rounded-lg overflow-hidden border-2 border-vermillion/20" style={{ paddingBottom: "56.25%" }}>
              <iframe
                src={listing.virtual_tour_url}
                className="absolute top-0 left-0 w-full h-full border-0"
                allow="fullscreen; vr"
                title="360 Virtual Tour"
                loading="lazy"
              />
            </div>
          </div>
        )}

        {/* Agent Attribution */}
        {listing.agent && (
          <div className="border border-ink/15 bg-white p-6 md:p-8 mb-12">
            <div className="flex items-start gap-5">
              {listing.agent.photo_url ? (
                <img src={listing.agent.photo_url} alt={listing.agent.name} className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 bg-vermillion flex items-center justify-center rounded-full">
                  <User className="w-6 h-6 text-oat" />
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-heading text-lg uppercase tracking-[0.12em]">{listing.agent.name}</h3>
                <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-ink/50 mt-1">{listing.agent.brokerage || "Real Estate Professional"}</p>
                <div className="flex flex-wrap gap-3 mt-4">
                  {listing.agent.phone && (
                    <a href={`tel:${listing.agent.phone}`} className="flex items-center gap-2 text-sm border border-ink/20 px-3 py-1.5 hover:border-vermillion transition">
                      <Phone className="w-3 h-3" /> {listing.agent.phone}
                    </a>
                  )}
                  {listing.agent.email && (
                    <a href={`mailto:${listing.agent.email}`} className="flex items-center gap-2 text-sm border border-ink/20 px-3 py-1.5 hover:border-vermillion transition">
                      <Mail className="w-3 h-3" /> Email Agent
                    </a>
                  )}
                  {listing.agent.public_url && (
                    <a href={listing.agent.public_url} className="flex items-center gap-2 text-sm border border-ink/20 px-3 py-1.5 hover:border-vermillion transition">
                      <User className="w-3 h-3" /> Agent Page
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lead Capture — Book a Showing */}
        <div className="bg-oat border border-ink/15 p-8 md:p-12 mb-12">
          <div className="max-w-xl mx-auto text-center">
            <h3 className="font-display text-2xl md:text-3xl mb-3">Want to see this home?</h3>
            <p className="text-ink/60 mb-6">Book a showing or get pre-approved — we'll connect you with a local agent within 24 hours.</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              try {
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/showing`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ name: fd.get("name"), email: fd.get("email"), phone: fd.get("phone"), message: fd.get("message") }),
                });
              } catch {}
              setLeadSubmitted(true);
            }} className="space-y-4">
              {leadSubmitted ? (
                <div className="bg-coal text-oat p-6 text-center">
                  <p className="font-heading text-lg mb-2">We'll be in touch within 24 hours!</p>
                  <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-oat/60">A local agent will reach out shortly</p>
                </div>
              ) : (
                <>
                  <input required type="text" name="name" placeholder="Your name" className="w-full px-4 py-3 border border-ink/20 font-body" />
                  <input required type="email" name="email" placeholder="Email address" className="w-full px-4 py-3 border border-ink/20 font-body" />
                  <input type="tel" name="phone" placeholder="Phone (optional)" className="w-full px-4 py-3 border border-ink/20 font-body" />
                  <input type="text" name="message" placeholder="Message (optional)" className="w-full px-4 py-3 border border-ink/20 font-body" />
                  <button type="submit" className="w-full btn-vermillion px-6 py-4 font-heading text-sm uppercase tracking-[0.15em]">
                    Book a Showing →
                  </button>
                </>
              )}
            </form>
            <p className="mt-4 font-mono text-[10px] tracking-[0.15em] uppercase text-ink/40">
              Get pre-approved faster → <a href="https://www.rocketmortgage.com" target="_blank" rel="noopener" className="text-vermillion hover:underline">Rocket Mortgage</a>
            </p>
          </div>
        </div>

        {/* Bottom CTA — the conversion driver */}
        <div className="bg-coal text-oat p-8 md:p-12 text-center border border-ink/30">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.1] mb-4">
            <span className="italic">Your</span> next listing —{" "}
            <span className="text-vermillion">8 seconds away.</span>
          </h2>
          <p className="font-body text-oat/75 mb-8 max-w-xl mx-auto">
            Paste your draft, pick a tone, hit rewrite. MLS + Instagram + Facebook + 5 headlines + email blast — all in your voice. First 3 are free.
          </p>
          <Link
            to="/#playground"
            data-testid="share-cta-bottom"
            className="inline-flex items-center gap-2 btn-vermillion px-8 py-4 font-heading text-sm uppercase tracking-[0.18em] hover:-translate-y-0.5 transition-transform"
          >
            Try Free at listworks.pro
            <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
          </Link>
        </div>
      </main>
    </div>
  );
}
